import { addSeconds } from 'date-fns/addSeconds';
import { differenceInMinutes } from 'date-fns/differenceInMinutes';
import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { toDate } from 'date-fns/toDate';

import { adjustedEndOfDay, adjustedStartOfDay, isAdjustedSameDay } from './adjusted-time';
import { isCardDeleted, studyStatesOf, updateTagsAfterEditing } from './card-utils';
import { composeProgressIndicatorsData } from './progress-indicators';
import {
  cardSortedByCreationDate,
  cardSorterByDueDate,
  cardSorterByPDFPosition,
  cardSorterBySortKey,
  createDetermisticCardShuffler,
  deckByDeck,
} from './sort-and-shuffle';
import type {
  CardLabel,
  DeckDailyProgressData,
  ProgressBarData,
  ProgressData,
  SessionStudyAction,
  SpacedRepetitionCard,
  SpacedRepetitionConfig,
  StudyOrder,
} from './types';

// Lives in card-utils (a leaf) so progress-indicators and sort-and-shuffle,
// which this module imports, can read it too without a cycle.
export { studyStatesOf } from './card-utils';

export function isMemorisedCard(card: SpacedRepetitionCard, endOfDay: Date): boolean {
  if (isCardDeleted(card)) return false;
  if (isCardNew(card)) return false;
  if (isCardLeech(card)) return false;
  // Unreadable last scheduling entry (corrupt/legacy row): the card is
  // unschedulable, not "memorised" — memorised cards can re-enter the queue
  // (group 5) and grading an unreadable state would crash next-card-state.
  if (!lastAlgoInfo(card)) return false;

  return !isCardDue(card, endOfDay);
}

function isEligibleForGroup5(
  card: SpacedRepetitionCard,
  endOfDay: Date,
  sessionStartTime: Date,
): boolean {
  // A Critical card that is not due today is memorised in every sense but its
  // label. Group 5 serves it too, or a Critical (or Starred) filter launched
  // after the card was graded Good would open straight onto "done for today".
  const scheduledCard = isCardLeech(card)
    ? { ...card, tags: updateTagsAfterEditing(card.tags ?? []) }
    : card;
  if (!isMemorisedCard(scheduledCard, endOfDay)) return false;

  const lastStudied = getCardLastStudiedTime(card);
  if (!lastStudied) return false;

  return isBefore(lastStudied, sessionStartTime);
}

/**
 * The last scheduling entry's algoInfo, or `undefined` when it is unreadable —
 * empty history, or a corrupt/legacy row whose last entry is missing/has no
 * algoInfo. Classification treats such cards as unschedulable (no phase, not
 * due) so ONE malformed row degrades to "excluded from today's queue" instead
 * of crashing the entire study screen for the deck. The card stays visible in
 * the deck list, where it can be edited or deleted.
 *
 * Exported for the per-deck study summary (`sync-client/deck-summary`), which
 * must classify a card exactly as the queue does — re-implementing the
 * corrupt-row handling there would be a second copy of this rule.
 */
export function lastAlgoInfo(
  card: Pick<SpacedRepetitionCard, 'studyState'>,
): SpacedRepetitionCard['studyState'][number]['algoInfo'] | undefined {
  const states = studyStatesOf(card);
  const last = states[states.length - 1] as
    | SpacedRepetitionCard['studyState'][number]
    | undefined;
  return last?.algoInfo ?? undefined;
}

export function isCardDue(card: SpacedRepetitionCard, deadline: Date) {
  if (isCardDeleted(card)) {
    return false;
  }
  if (studyStatesOf(card).length === 0) {
    return false;
  }
  const lastStateDue = lastAlgoInfo(card)?.nextDueISO;
  if (!lastStateDue) {
    return false;
  }

  if (isBefore(lastStateDue, deadline)) {
    return true;
  }
  return false;
}

/**
 * @param endOfDay Optional day cutoff, so a caller labelling MANY cards can
 *   compute it once instead of per card. Optional for source compatibility —
 *   every existing call keeps working — but pass it from any loop: this is the
 *   most expensive per-card walk on the deck screen, and the cutoff was ~5
 *   `Date` allocations of it.
 *
 *   Sharing one instant across a pass is also more correct than deriving it per
 *   card: a batch that straddles the 4 am boundary used to label its first
 *   cards against yesterday's deadline and its last against today's.
 */
export function getCardLabel(
  card: SpacedRepetitionCard,
  endOfDay: Date = adjustedEndOfDay(new Date(), { hours: 4 }),
): CardLabel | undefined {
  // `isCardDeleted` once: `isCardNew`, `isCardLeech` and `isCardDue` each
  // re-walk `actions` for it, and past this line the card is known live.
  if (isCardDeleted(card)) {
    return undefined;
  }

  if (studyStatesOf(card).length === 0) {
    return 'new';
  }

  if (card.tags?.find((tag) => tag.type === 'leech') !== undefined) {
    return 'leech';
  }

  const lastStateDue = lastAlgoInfo(card)?.nextDueISO;
  if (lastStateDue && isBefore(lastStateDue, endOfDay)) {
    return 'toReview';
  }

  return 'memorised';
}

/**
 * The label/to-do inputs of one card, which depend on the card alone — the
 * clock enters only as the cutoff `dueMs` is compared with.
 */
export interface CardLabelFacts {
  /** `isCardDeleted(card)`. */
  deleted: boolean;
  /** `isCardNew(card)`. */
  isNew: boolean;
  /** `isCardLeech(card)`. */
  leech: boolean;
  /** The instant `isCardDue` compares with its deadline (`isBefore` coerces
   *  with `toDate`), or NaN when `isCardDue` is false whatever the deadline
   *  (deleted, no history, no `nextDueISO`). `isCardDue(card, d)` ≡
   *  `dueMs < d.getTime()`. */
  dueMs: number;
}

/** Uncached {@link cardLabelFacts} — exported for the equality pins. */
export function computeCardLabelFacts(card: SpacedRepetitionCard): CardLabelFacts {
  const deleted = isCardDeleted(card);
  if (deleted) return { deleted, isNew: false, leech: false, dueMs: NaN };
  const hasState = studyStatesOf(card).length > 0;
  const leech = card.tags?.find((tag) => tag.type === 'leech') !== undefined;
  let dueMs = NaN;
  if (hasState) {
    const lastStateDue = lastAlgoInfo(card)?.nextDueISO;
    if (lastStateDue) dueMs = +toDate(lastStateDue);
  }
  return { deleted, isNew: !hasState, leech, dueMs };
}

/**
 * Cached per card OBJECT. Cards are immutable once they reach a consumer —
 * every edit builds a new card (the store's identity-based dirty scan depends
 * on it) — so the facts of one object never change; a list re-counted on
 * every render (the deck top bar, the to-do pill) then pays one WeakMap read
 * per card instead of a `Date` parse and three `actions` walks.
 */
const labelFactsByCard = new WeakMap<object, CardLabelFacts>();

export function cardLabelFacts(card: SpacedRepetitionCard): CardLabelFacts {
  let facts = labelFactsByCard.get(card);
  if (facts === undefined) {
    facts = computeCardLabelFacts(card);
    labelFactsByCard.set(card, facts);
  }
  return facts;
}

export function isCardNew(card: SpacedRepetitionCard) {
  return !isCardDeleted(card) && studyStatesOf(card).length === 0;
}

export function isCardInLearningPhase(card: SpacedRepetitionCard) {
  return (
    !isCardDeleted(card) &&
    (studyStatesOf(card).length === 0 || lastAlgoInfo(card)?.studyPhase === 'learn')
  );
}

export function isCardInLapsedPhase(card: SpacedRepetitionCard) {
  return (
    !isCardDeleted(card) &&
    studyStatesOf(card).length > 0 &&
    lastAlgoInfo(card)?.studyPhase === 'lapsed'
  );
}

export function isCardInReviewPhase(card: SpacedRepetitionCard) {
  return (
    !isCardDeleted(card) &&
    studyStatesOf(card).length > 0 &&
    lastAlgoInfo(card)?.studyPhase === 'review'
  );
}

export function isCardLeech(
  card: Pick<SpacedRepetitionCard, 'deletedTimeISO' | 'actions' | 'tags'>,
) {
  return (
    !isCardDeleted(card) && card.tags?.find((tag) => tag.type === 'leech') !== undefined
  );
}

export function isCardToBeRestudiedToday(
  card: SpacedRepetitionCard,
  now: Date,
  spacedRepetitionConfig: SpacedRepetitionConfig,
) {
  const states = studyStatesOf(card);
  if (isCardDeleted(card) || states.length === 0) {
    return false;
  }

  const algoInfo = lastAlgoInfo(card);

  if (
    !algoInfo ||
    algoInfo.type !== 'anki' ||
    algoInfo.studyPhase === 'review' ||
    isBefore(
      now,
      adjustedEndOfDay(new Date(algoInfo.nextDueISO), spacedRepetitionConfig.cutoffTime),
    )
  ) {
    return false;
  }

  const lastBadIndex = states.findLastIndex((studyState) => studyState.type === 'bad');

  const goodCountAfterBad = states
    .slice(lastBadIndex + 1)
    .map((studyState) => studyState.type === 'good').length;

  return goodCountAfterBad > 0;
}

export function isCardConcludedToday(
  card: SpacedRepetitionCard,
  now: Date,
  cutoffTime: { hours: number; minutes: number },
) {
  return isCardConcludedInDay(
    card,
    adjustedStartOfDay(now, cutoffTime),
    adjustedEndOfDay(now, cutoffTime),
  );
}

/**
 * {@link isCardConcludedToday} for an explicit SR day window — last studied
 * inside `[dayStart, dayEnd]` and not due again before the day is over.
 *
 * The window is the caller's so the same rule can run where "today" is not
 * the runtime's local day: the sync worker answers for a user in their own
 * time zone from an isolate that lives in UTC.
 */
export function isCardConcludedInDay(
  card: SpacedRepetitionCard,
  dayStart: Date,
  dayEnd: Date,
) {
  const states = studyStatesOf(card);
  if (isCardDeleted(card) || states.length === 0) {
    return false;
  }

  // Consider only readable entries — corrupt/legacy rows can be SPARSE arrays
  // (holes) or carry entries without ISO/algoInfo; an unreadable history can't
  // conclude the card today.
  const readableStates = states.filter(
    (state): state is SpacedRepetitionCard['studyState'][number] =>
      typeof state?.ISO === 'string' && state.algoInfo != null,
  );
  if (readableStates.length === 0) {
    return false;
  }
  const lastStateIndex = argMax(
    readableStates.map((state) => new Date(state.ISO).getTime()),
  );
  const lastState = readableStates[lastStateIndex];
  const studiedDate = new Date(lastState.ISO);

  return (
    !isBefore(studiedDate, dayStart) &&
    !isAfter(studiedDate, dayEnd) &&
    isAfter(new Date(lastState.algoInfo.nextDueISO), dayEnd)
  );
}

function argMax(array: number[]): number {
  // Seeded reduce: an empty array — INCLUDING a sparse one, whose holes map()
  // preserves and reduce() then skips (corrupt studyState rows arrive as sparse
  // arrays) — returns -1 instead of throwing "reduce of empty array".
  return array
    .map((x, i) => [x, i])
    .reduce((r, a) => (a[0] > r[0] ? a : r), [-Infinity, -1])[1];
}

export function getNewCardsGloballyStudiedToday<TCard extends SpacedRepetitionCard>(
  decks: Array<{ cards: TCard[] }>,
  nowTimeISO: string,
  spacedRepetitionConfig: SpacedRepetitionConfig,
  getVisibleCards: (cards: TCard[]) => TCard[],
) {
  return decks.reduce((newCardStudied, deck) => {
    const visible = getVisibleCards(deck.cards);
    const x =
      newCardStudied +
      visible.filter(
        (card) =>
          !isCardDeleted(card) &&
          isCardFirstStudiedToday(card, nowTimeISO, spacedRepetitionConfig),
      ).length;
    return x;
  }, 0);
}

/**
 * The five groups the spaced-repetition queue is built from, UNSORTED — shared by
 * {@link getCardsToStudyNow} (which orders them) and {@link countCardsToStudyNow}
 * (which only needs their sizes, so it never pays for the sorts).
 *
 * Group 3 (new cards) is not yet capped at the daily new-card allowance: which
 * new cards make the cut depends on the order, how many does not.
 */
function partitionCardsToStudyNow<CardType extends SpacedRepetitionCard>(
  cards: CardType[],
  nowTimeISO: string,
  spacedRepetitionConfig: SpacedRepetitionConfig,
  newCardsStudiedTodayAllDecks: number,
  sessionStartISO: string | undefined,
  includeMemorisedCards: boolean,
) {
  const nowDateTime = new Date(nowTimeISO);
  const startOfDay = addSeconds(
    adjustedEndOfDay(nowDateTime, spacedRepetitionConfig.cutoffTime),
    1,
  );
  const endOfDay = adjustedEndOfDay(nowDateTime, spacedRepetitionConfig.cutoffTime);

  const existingCards = cards.filter((card) => !isCardDeleted(card));

  // Whole cards only, never NaN — exactly what `.slice(0, n)` makes of the raw
  // difference, so the count and the served queue agree on any config value.
  const newCardsRemainingToday =
    Math.max(
      0,
      Math.trunc(spacedRepetitionConfig.newCardsPerDay - newCardsStudiedTodayAllDecks),
    ) || 0;

  const sessionStartTime = sessionStartISO ? new Date(sessionStartISO) : null;

  // ONE classification pass (it used to be four filters re-running the phase and
  // due predicates, each re-parsing the due date). Same groups, same card order:
  //  - group1: learning/lapsed, due before now;
  //  - group2: review, due before the start of tomorrow;
  //  - new cards;
  //  - group4: learning/lapsed, due later today.
  const nowMs = nowDateTime.getTime();
  const startOfDayMs = startOfDay.getTime();
  const endOfDayMs = endOfDay.getTime();
  const group1: CardType[] = [];
  const group2: CardType[] = [];
  const newCards: CardType[] = [];
  const group4: CardType[] = [];
  for (const card of existingCards) {
    if (studyStatesOf(card).length === 0) {
      newCards.push(card);
      continue;
    }
    const info = lastAlgoInfo(card);
    if (!info?.nextDueISO) continue; // not due by any deadline (`isCardDue`)
    // `isBefore` semantics: an unreadable date is never before anything.
    const dueMs = new Date(info.nextDueISO).getTime();
    if (info.studyPhase === 'learn' || info.studyPhase === 'lapsed') {
      if (dueMs < nowMs) group1.push(card);
      else if (dueMs < endOfDayMs) group4.push(card);
    } else if (info.studyPhase === 'review') {
      if (dueMs < startOfDayMs) group2.push(card);
    }
  }

  let group5: CardType[] = [];
  if (includeMemorisedCards && sessionStartTime != null) {
    group5 = existingCards.filter((card) =>
      isEligibleForGroup5(card, endOfDay, sessionStartTime),
    );
  }

  return { group1, group2, newCards, newCardsRemainingToday, group4, group5 };
}

/**
 * `getCardsToStudyNow(...same arguments).length`, without ordering the queue — the
 * "Study N cards" launchers recount on every card write while they are open, and
 * the sorts are most of the cost of building the queue.
 */
export function countCardsToStudyNow<CardType extends SpacedRepetitionCard>(
  cards: CardType[],
  nowTimeISO: string,
  spacedRepetitionConfig: SpacedRepetitionConfig,
  newCardsStudiedTodayAllDecks: number,
  sessionStartISO?: string,
  includeMemorisedCards: boolean = false,
): number {
  const g = partitionCardsToStudyNow(
    cards,
    nowTimeISO,
    spacedRepetitionConfig,
    newCardsStudiedTodayAllDecks,
    sessionStartISO,
    includeMemorisedCards,
  );
  return (
    g.group1.length +
    g.group2.length +
    Math.min(g.newCards.length, g.newCardsRemainingToday) +
    g.group4.length +
    g.group5.length
  );
}

export function getCardsToStudyNow<CardType extends SpacedRepetitionCard>(
  cards: CardType[],
  nowTimeISO: string,
  spacedRepetitionConfig: SpacedRepetitionConfig,
  newCardsStudiedTodayAllDecks: number,
  shuffle: boolean = false,
  shuffleSeed: number = 0,
  sessionStartISO?: string,
  includeMemorisedCards: boolean = false,
  studyOrder: StudyOrder = 'default',
) {
  const partition = partitionCardsToStudyNow(
    cards,
    nowTimeISO,
    spacedRepetitionConfig,
    newCardsStudiedTodayAllDecks,
    sessionStartISO,
    includeMemorisedCards,
  );
  const { group4, group5 } = partition;
  // Due order is the default's final order for groups 1–2, and pdf-order's tiebreak
  // (its comparator ties cards without a position, and the sort is stable). A
  // shuffle and date-created (a total order) never read it, so they skip the sort.
  const dueOrderNeeded = !shuffle && studyOrder !== 'date-created';
  const group1 = dueOrderNeeded
    ? partition.group1.sort(cardSorterByDueDate)
    : partition.group1;
  const group2 = dueOrderNeeded
    ? partition.group2.sort(cardSorterByDueDate)
    : partition.group2;

  // Document positions and sortKeys are per deck: a folder keeps its decks
  // apart (`deckByDeck`). Creation dates and due dates compare across decks.
  const group3Sorter =
    studyOrder === 'pdf-order'
      ? deckByDeck(cards, cardSorterByPDFPosition)
      : studyOrder === 'date-created'
        ? cardSortedByCreationDate
        : deckByDeck(cards, cardSorterBySortKey);

  const group3 = partition.newCards
    .sort(group3Sorter)
    .slice(0, partition.newCardsRemainingToday);

  if (shuffle) {
    const baseShuffleSeed = shuffleSeed * 3;
    const deterministicShufflerGroup1 = createDetermisticCardShuffler(
      new Date(nowTimeISO),
      spacedRepetitionConfig.cutoffTime,
      baseShuffleSeed,
    );
    const deterministicShufflerGroup23 = createDetermisticCardShuffler(
      new Date(nowTimeISO),
      spacedRepetitionConfig.cutoffTime,
      baseShuffleSeed + 1,
    );
    const deterministicShufflerGroup4 = createDetermisticCardShuffler(
      new Date(nowTimeISO),
      spacedRepetitionConfig.cutoffTime,
      baseShuffleSeed + 2,
    );
    const deterministicShufflerGroup5 = createDetermisticCardShuffler(
      new Date(nowTimeISO),
      spacedRepetitionConfig.cutoffTime,
      baseShuffleSeed + 3,
    );

    const shuffledGroup1 = group1.sort(deterministicShufflerGroup1);
    const shuffledGroup23 = [...group2, ...group3].sort(deterministicShufflerGroup23);
    const shuffledGroup4 = group4.sort(deterministicShufflerGroup4);
    const shuffledGroup5 = group5.sort(deterministicShufflerGroup5);

    return [...shuffledGroup1, ...shuffledGroup23, ...shuffledGroup4, ...shuffledGroup5];
  }
  // `group3` is already in its final order: it was sorted by this same order's
  // comparator to pick which new cards make today's allowance.
  if (studyOrder === 'pdf-order') {
    return [
      ...group1.sort(group3Sorter),
      ...group2.sort(group3Sorter),
      ...group3,
      ...group4.sort(group3Sorter),
      ...group5.sort(group3Sorter),
    ];
  }

  if (studyOrder === 'date-created') {
    return [
      ...group1.sort(cardSortedByCreationDate),
      ...group2.sort(cardSortedByCreationDate),
      ...group3,
      ...group4.sort(cardSortedByCreationDate),
      ...group5.sort(cardSortedByCreationDate),
    ];
  }

  return [
    // Groups 1–2 are in due order already (above).
    ...group1,
    ...group2,
    // New cards on 'default' follow the deck's canonical sortKey order —
    // the order the user sees (and arranges) in the flashcards view.
    // 'date-created' remains available as its own explicit option.
    ...group3,
    ...group4.sort(cardSorterByDueDate),
    ...group5.sort(cardSorterByDueDate),
  ];
}

function isCardFirstStudiedToday(
  card: SpacedRepetitionCard,
  todayISO: string,
  spacedRepetitionConfig: SpacedRepetitionConfig,
) {
  const states = studyStatesOf(card);
  if (states.length === 0 || card.deletedTimeISO != null) {
    return false;
  }
  const firstStudyState = states[0];

  const firstStudiedTime = firstStudyState.ISO;

  return isAdjustedSameDay(
    new Date(firstStudiedTime),
    new Date(todayISO),
    spacedRepetitionConfig.cutoffTime,
  );
}

export function getCardLastStudiedTime(card: SpacedRepetitionCard) {
  // Skip unreadable entries (corrupt/legacy rows) instead of throwing on x.ISO.
  const times = studyStatesOf(card)
    .filter(
      (x): x is SpacedRepetitionCard['studyState'][number] => typeof x?.ISO === 'string',
    )
    .map((x) => new Date(x.ISO).getTime());
  if (times.length === 0) {
    return undefined;
  }
  return new Date(Math.max(...times));
}

export function getCurrentProgressData<TCard extends SpacedRepetitionCard>(
  allCards: TCard[],
  cardQueue: TCard[],
  todayISO: string,
  spacedRepetitionConfig: SpacedRepetitionConfig,
): DeckDailyProgressData {
  const now = new Date(todayISO);

  const studiedToday = allCards.filter((card) =>
    isCardConcludedToday(card, now, spacedRepetitionConfig.cutoffTime),
  );

  const minutesRemainingToday = differenceInMinutes(
    adjustedEndOfDay(now, spacedRepetitionConfig.cutoffTime),
    now,
  );

  const graduationStepsHappeningToday = Math.max(
    1,
    spacedRepetitionConfig.graduationStepsMinutes.filter(
      (step) => step < minutesRemainingToday,
    ).length,
  );

  const relearningStepsHappeningToday = Math.max(
    1,
    spacedRepetitionConfig.relearningStepsMinutes.filter(
      (step) => step < minutesRemainingToday,
    ).length,
  );

  const stepsToDoToday = cardQueue.reduce((totalSteps, currCard) => {
    const states = studyStatesOf(currCard);
    if (states.length === 0) {
      return totalSteps + graduationStepsHappeningToday;
    }

    const lastStepState = states[states.length - 1];

    if (lastStepState.algoInfo.studyPhase === 'review') {
      return totalSteps + 1;
    }
    const lastBadIndex = states.findLastIndex((state) => state.type === 'bad');
    const goodsAfterBad = states
      .slice(lastBadIndex + 1)
      .filter((state) => state.type === 'good').length;

    const goodsNeeded =
      lastStepState.algoInfo.studyPhase === 'learn'
        ? graduationStepsHappeningToday
        : relearningStepsHappeningToday;

    return totalSteps + Math.max(1, goodsNeeded - goodsAfterBad);
  }, 0);

  const stepsDoneToday = allCards.reduce((totalSteps, currCard) => {
    // findLastIndex VISITS holes of a sparse array (unlike map/filter), so the
    // callback must tolerate undefined entries — corrupt studyState rows arrive
    // as sparse arrays, and this runs over ALL cards, not just the queue.
    const states = studyStatesOf(currCard);
    const lastStepBeforeTodayIndex = states.findLastIndex(
      (studyState) =>
        typeof studyState?.ISO === 'string' &&
        isBefore(
          new Date(studyState.ISO),
          adjustedStartOfDay(now, spacedRepetitionConfig.cutoffTime),
        ),
    );

    const bads = states
      .slice(lastStepBeforeTodayIndex >= 0 ? lastStepBeforeTodayIndex : 0)
      // `state?.` — filter skips sparse holes but still visits explicit
      // undefined elements (corrupt rows contain both shapes).
      .filter((state) => state?.type === 'bad').length;

    const steps = states.length - 1 - lastStepBeforeTodayIndex - bads;
    return totalSteps + steps;
  }, 0);

  const queueLength = cardQueue.length;
  const cardToDoNotSeenToday = cardQueue.filter((card) => {
    const lastStudiedTime = getCardLastStudiedTime(card);
    if (lastStudiedTime == null) {
      return true;
    }
    return !isAdjustedSameDay(lastStudiedTime, now, spacedRepetitionConfig.cutoffTime);
  }).length;

  return {
    cardsToDoNotSeenToday: cardToDoNotSeenToday,
    cardsToDoAlreadySeenToday: queueLength - cardToDoNotSeenToday,
    cardsRemainingToday: queueLength,
    cardsDoneToday: studiedToday.length,
    stepsToDoToday,
    stepsDoneToday,
    estTimeRemainingMinutesToday: stepsToDoToday / 3,
  };
}

export function getProgressBarData<TCard extends SpacedRepetitionCard>(
  cardQueue: TCard[],
  spacedRepetitionConfig: SpacedRepetitionConfig,
  sessionStudyActions: Array<SessionStudyAction<TCard>>,
): ProgressBarData {
  const stepsCompletedInSession = sessionStudyActions.filter(
    (a) => a.status !== 'bad',
  ).length;

  const graduationStepsPerCard = Math.max(
    1,
    spacedRepetitionConfig.graduationStepsMinutes.length,
  );

  const relearningStepsPerCard = Math.max(
    1,
    spacedRepetitionConfig.relearningStepsMinutes.length,
  );

  const stepsRemainingInSession = cardQueue.reduce((totalSteps, currCard) => {
    const states = studyStatesOf(currCard);
    if (states.length === 0) {
      return totalSteps + graduationStepsPerCard;
    }

    const lastStepState = states[states.length - 1];

    if (lastStepState.algoInfo.studyPhase === 'review') {
      return totalSteps + 1;
    }

    const lastBadIndex = states.findLastIndex((state) => state.type === 'bad');
    const goodsAfterBad = states
      .slice(lastBadIndex + 1)
      .filter((state) => state.type === 'good').length;

    const goodsNeeded =
      lastStepState.algoInfo.studyPhase === 'learn'
        ? graduationStepsPerCard
        : relearningStepsPerCard;

    return totalSteps + Math.max(1, goodsNeeded - goodsAfterBad);
  }, 0);

  return {
    stepsRemainingInSession,
    stepsCompletedInSession,
    estMinutesRemainingInSession: stepsRemainingInSession / 3,
  };
}

export function composeProgressData<TCard extends SpacedRepetitionCard>(
  progressBarData: ProgressBarData,
  cardQueue: TCard[],
  cardsStudiedThisSession: number,
  sessionStartISO: string,
): ProgressData {
  return {
    ...progressBarData,
    ...composeProgressIndicatorsData(cardQueue, cardsStudiedThisSession, sessionStartISO),
  };
}
